import {
  getOrderStatusText,
  getOrderStatusCode,
  getOrderStatusBadgeClass,
} from "./orderStatus";
import { getOrderPaymentSummary } from "./orderPaymentSummary";
import { getNaverPoint } from "./orderPathFilter";

export interface PaymentMethodDetail {
  code: string;
  name: string;
  amount: number;
  formattedAmount: string;
  category:
    | "payment"
    | "coupon"
    | "mileage"
    | "deposit"
    | "naver_point"
    | "discount";
  categoryLabel: string;
  badgeClass: string;
  isDiscount: boolean;
  statusText?: string;
}

export interface ClaimItemInfo {
  product_name?: string;
  item_no?: string | number;
  product_no?: string | number;
  order_item_code?: string;
  product_code?: string;
  quantity?: number;
  claim_quantity?: number;
  product_price?: number;
  option_price?: number;
  option_value?: string;
  option_code?: string;
  claim_code?: string;
  order_status?: string;
  status_text?: string;
  status_code?: string;
}

export interface ClaimRecord {
  type: "cancel" | "return" | "exchange";
  typeLabel: string;
  claim_code?: string;
  date?: string;
  complete_date?: string;
  reason?: string;
  reason_detail?: string;
  status?: string;
  status_text?: string;
  status_badge_class?: string;
  refund_amount?: number;
  refund_method?: string;
  additional_payment_amount?: number;
  items: ClaimItemInfo[];
  isSynthesizedFromItems?: boolean;
}

export interface OrderClaimDetails {
  hasClaims: boolean;
  hasCancellations: boolean;
  hasReturns: boolean;
  hasExchanges: boolean;
  isFullCancelled: boolean;
  isPartialCancelled: boolean;
  overallStatusText: string;
  overallStatusBadgeClass: string;
  cancellations: ClaimRecord[];
  returns: ClaimRecord[];
  exchanges: ClaimRecord[];
  cancelledItemCount: number;
  returnedItemCount: number;
  exchangeItemCount: number;
  totalItemCount: number;
  activeItemCount: number;
  reducedPaymentAmount: number;
  reducedOrderCompositionAmount: number;
  itemLevelClaims: ClaimItemInfo[];
}

export function toNum(val: unknown): number {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number" && Number.isFinite(val)) return val;
  const parsed = Number(String(val).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function optionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text.length > 0 ? text : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeStatus(source: any): {
  status?: string;
  status_text?: string;
  status_badge_class?: string;
} {
  const rawStatus =
    source?.status_code ?? source?.order_status ?? source?.status;
  const status = optionalString(rawStatus);
  const statusCode = status ? getOrderStatusCode(status) : undefined;

  return {
    status: statusCode,
    status_text:
      optionalString(source?.status_text) ||
      (statusCode ? getOrderStatusText(statusCode) : undefined),
    status_badge_class: statusCode
      ? getOrderStatusBadgeClass(statusCode)
      : undefined,
  };
}

export function formatPrice(val: unknown): string {
  return Math.abs(toNum(val)).toLocaleString();
}

/**
 * Extracts and allocates detailed payment methods and their respective amounts
 * (e.g., Credit Card: 61,100 KRW, Coupon: 10,000 KRW, Points: 2,000 KRW, etc.).
 */
export function getDetailedPaymentMethods(item: any): PaymentMethodDetail[] {
  if (!item || typeof item !== "object") return [];

  const isRefund = Array.isArray(item?.refund_methods);

  // Case 1: Refund record
  if (isRefund) {
    const list: PaymentMethodDetail[] = [];
    const actualRefundAmount = toNum(item?.actual_refund_amount);
    const refundMethods =
      Array.isArray(item?.refund_methods) && item.refund_methods.length > 0
        ? item.refund_methods.filter(Boolean).map(String).join(", ")
        : "환불";
    const usedPoints = toNum(item?.used_points);
    const usedCredits = toNum(item?.used_credits);
    const naverPoint = toNum(item?.naver_point);

    if (
      actualRefundAmount > 0 ||
      (usedPoints === 0 && usedCredits === 0 && naverPoint === 0)
    ) {
      list.push({
        code: "refund_cash",
        name: refundMethods || "실제 환불액",
        amount: actualRefundAmount,
        formattedAmount: `${formatPrice(actualRefundAmount)}원`,
        category: "payment",
        categoryLabel: "실제 환불액 (PG/계좌)",
        badgeClass: "badge-red",
        isDiscount: false,
        statusText: "환불완료",
      });
    }

    if (usedPoints > 0) {
      list.push({
        code: "refund_points",
        name: "적립금 복원",
        amount: usedPoints,
        formattedAmount: `${formatPrice(usedPoints)}원`,
        category: "mileage",
        categoryLabel: "적립금 환불",
        badgeClass: "badge-warning",
        isDiscount: false,
        statusText: "복원완료",
      });
    }

    if (usedCredits > 0) {
      list.push({
        code: "refund_credits",
        name: "예치금 복원",
        amount: usedCredits,
        formattedAmount: `${formatPrice(usedCredits)}원`,
        category: "deposit",
        categoryLabel: "예치금 환불",
        badgeClass: "badge-secondary",
        isDiscount: false,
        statusText: "복원완료",
      });
    }

    if (naverPoint > 0) {
      list.push({
        code: "refund_naver_point",
        name: "네이버 포인트 환불",
        amount: naverPoint,
        formattedAmount: `${formatPrice(naverPoint)}원`,
        category: "naver_point",
        categoryLabel: "네이버포인트 환불",
        badgeClass: "badge-blue",
        isDiscount: false,
        statusText: "환불완료",
      });
    }

    return list;
  }

  // Case 2: Order record
  // 1. First check if explicit payment components with amounts are available
  const rawComponents =
    item?.payment_components ||
    item?.order?.payment_components ||
    item?.actual_order_amount?.payment_components;

  if (Array.isArray(rawComponents) && rawComponents.length > 0) {
    const validWithAmount = rawComponents.filter(
      (c) => c && (c.amount !== undefined || c.payment_amount !== undefined)
    );
    if (validWithAmount.length > 0) {
      return validWithAmount.map((c, idx) => {
        const amount = toNum(c.amount ?? c.payment_amount);
        const name = String(
          c.name || c.payment_method_name || c.code || `결제수단 ${idx + 1}`
        );
        const code = String(
          c.code || c.payment_method || `comp_${idx}`
        ).toLowerCase();
        const isCoupon = code.includes("coupon") || name.includes("쿠폰");
        const isMileage =
          code.includes("mileage") ||
          code.includes("point") ||
          name.includes("적립금");
        const isDeposit =
          code.includes("deposit") ||
          code.includes("credit") ||
          name.includes("예치금");
        const isNaver = code.includes("naver") || name.includes("네이버");

        let category: PaymentMethodDetail["category"] = "payment";
        let badgeClass = "badge-blue";
        if (isCoupon) {
          category = "coupon";
          badgeClass = "badge-warning";
        } else if (isMileage) {
          category = "mileage";
          badgeClass = "badge-secondary";
        } else if (isDeposit) {
          category = "deposit";
          badgeClass = "badge-secondary";
        } else if (isNaver) {
          category = "naver_point";
          badgeClass = "badge-blue";
        }

        return {
          code,
          name,
          amount,
          formattedAmount: `${formatPrice(amount)}원`,
          category,
          categoryLabel: isCoupon ? "쿠폰할인" : "결제",
          badgeClass,
          isDiscount: isCoupon,
          statusText: "승인완료",
        };
      });
    }
  }

  // 2. Synthesize breakdown from order fields and payment summary
  const paymentSummary = getOrderPaymentSummary(item);
  const actualAmountSource = item?.actual_order_amount || {};
  const initialAmountSource = item?.initial_order_amount || {};

  // Amounts
  const couponAmount =
    paymentSummary.actualCouponAmount ||
    paymentSummary.initialCouponAmount ||
    toNum(
      actualAmountSource.coupon_discount_price ||
        actualAmountSource.coupon_discount_amount
    ) ||
    toNum(
      initialAmountSource.coupon_discount_price ||
        initialAmountSource.coupon_discount_amount
    );

  const pointsAmount = toNum(
    actualAmountSource.points_spent_amount ??
      initialAmountSource.points_spent_amount
  );
  const creditsAmount = toNum(
    actualAmountSource.credits_spent_amount ??
      initialAmountSource.credits_spent_amount
  );
  const naverPointAmount = getNaverPoint(item);

  // Primary payment amount (PG, cash, card, etc.)
  const primaryAmount =
    paymentSummary.actualPaymentAmount ||
    paymentSummary.initialPaymentAmount ||
    toNum(
      actualAmountSource.payment_amount ??
        item?.payment_amount ??
        initialAmountSource.payment_amount
    );

  const rawMethodName = String(
    item?.payment_method_name ||
      item?.order?.payment_method_name ||
      item?.payment_method ||
      ""
  ).trim();

  const tokens = rawMethodName
    .split(/[,/|+]|\s+\/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const list: PaymentMethodDetail[] = [];

  // Primary non-discount payment method name
  const isExcludedFromPrimary = (t: string) => {
    const lower = t.toLowerCase();
    return (
      lower.includes("쿠폰") ||
      lower.includes("coupon") ||
      lower.includes("적립금") ||
      lower.includes("mileage") ||
      lower.includes("예치금") ||
      lower.includes("deposit") ||
      lower.includes("크레딧") ||
      lower.includes("credit") ||
      lower.includes("포인트") ||
      lower.includes("point")
    );
  };

  const primaryMethodToken =
    tokens.find((t) => !isExcludedFromPrimary(t)) || "신용카드";

  if (
    primaryAmount > 0 ||
    (tokens.length > 0 && !tokens.every(isExcludedFromPrimary))
  ) {
    list.push({
      code: "primary_payment",
      name: primaryMethodToken,
      amount: primaryAmount,
      formattedAmount: `${formatPrice(primaryAmount)}원`,
      category: "payment",
      categoryLabel: "실결제 (승인)",
      badgeClass: "badge-blue",
      isDiscount: false,
      statusText: "승인완료",
    });
  }

  // Coupon
  const hasCouponToken = tokens.some(
    (t) => t.includes("쿠폰") || t.toLowerCase().includes("coupon")
  );
  if (couponAmount > 0 || hasCouponToken) {
    list.push({
      code: "coupon",
      name: "쿠폰",
      amount: couponAmount,
      formattedAmount: `${formatPrice(couponAmount)}원`,
      category: "coupon",
      categoryLabel: "쿠폰할인",
      badgeClass: "badge-warning",
      isDiscount: true,
      statusText: "적용완료",
    });
  }

  // Points (적립금)
  const hasPointsToken = tokens.some(
    (t) => t.includes("적립금") || t.toLowerCase().includes("mileage")
  );
  if (pointsAmount > 0 || hasPointsToken) {
    list.push({
      code: "mileage",
      name: "적립금",
      amount: pointsAmount,
      formattedAmount: `${formatPrice(pointsAmount)}원`,
      category: "mileage",
      categoryLabel: "적립금 사용",
      badgeClass: "badge-secondary",
      isDiscount: false,
      statusText: "사용완료",
    });
  }

  // Credits (예치금)
  const hasCreditsToken = tokens.some(
    (t) =>
      t.includes("예치금") ||
      t.toLowerCase().includes("deposit") ||
      t.toLowerCase().includes("credit")
  );
  if (creditsAmount > 0 || hasCreditsToken) {
    list.push({
      code: "deposit",
      name: "예치금",
      amount: creditsAmount,
      formattedAmount: `${formatPrice(creditsAmount)}원`,
      category: "deposit",
      categoryLabel: "예치금 사용",
      badgeClass: "badge-secondary",
      isDiscount: false,
      statusText: "사용완료",
    });
  }

  // Naver point
  if (naverPointAmount > 0) {
    list.push({
      code: "naver_point",
      name: "네이버 포인트",
      amount: naverPointAmount,
      formattedAmount: `${formatPrice(naverPointAmount)}원`,
      category: "naver_point",
      categoryLabel: "네이버포인트 사용",
      badgeClass: "badge-blue",
      isDiscount: false,
      statusText: "사용완료",
    });
  }

  // Fallback if list is empty
  if (list.length === 0) {
    list.push({
      code: "default",
      name: rawMethodName || "정보 없음",
      amount: primaryAmount,
      formattedAmount: `${formatPrice(primaryAmount)}원`,
      category: "payment",
      categoryLabel: "결제",
      badgeClass: "badge-secondary",
      isDiscount: false,
    });
  }

  return list;
}

/**
 * Inspects all claims (cancellation, return, exchange) and item-level claim statuses,
 * keeping cancellation, return, and exchange strictly separated to match Cafe24 order embed data.
 */
export function getOrderClaimDetails(item: any): OrderClaimDetails {
  const isRefund = Array.isArray(item?.refund_methods);
  const targetOrder = isRefund && item?.order ? item.order : item;

  const rawCancellations =
    targetOrder?.cancellation || targetOrder?.cancellations || [];
  const rawReturns = targetOrder?.return || targetOrder?.returns || [];
  const rawExchanges = targetOrder?.exchange || targetOrder?.exchanges || [];

  const rawItems: any[] = Array.isArray(targetOrder?.items)
    ? targetOrder.items
    : Array.isArray(item?.items)
    ? item.items
    : [];

  // Normalize only the fields present on each embedded claim item.
  // Do not fill missing values from the order item or invent display text.
  const mapClaimItems = (claimItems: any[]): ClaimItemInfo[] => {
    if (!Array.isArray(claimItems)) return [];
    return claimItems.map((ci) => {
      const status = normalizeStatus(ci);

      return {
        product_name: optionalString(ci.product_name),
        item_no: ci.item_no,
        product_no: ci.product_no,
        order_item_code: optionalString(ci.order_item_code),
        product_code: optionalString(ci.product_code),
        quantity: optionalNumber(ci.quantity),
        claim_quantity: optionalNumber(ci.claim_quantity),
        product_price: optionalNumber(ci.product_price),
        option_price: optionalNumber(ci.option_price),
        option_value: optionalString(ci.option_value),
        option_code: optionalString(ci.option_code),
        claim_code: optionalString(ci.claim_code),
        order_status: status.status,
        status_text: status.status_text,
        status_code: status.status,
      };
    });
  };

  const paymentSummary = getOrderPaymentSummary(targetOrder);
  const reducedPaymentAmount = Math.max(
    0,
    paymentSummary.initialPaymentAmount - paymentSummary.actualPaymentAmount
  );
  const reducedOrderCompositionAmount = Math.max(
    0,
    paymentSummary.initialOrderCompositionAmount -
      paymentSummary.actualOrderCompositionAmount
  );

  // 1. Process Cafe24 Embed Cancellation
  const cancellations: ClaimRecord[] = (
    Array.isArray(rawCancellations) ? rawCancellations : [rawCancellations]
  )
    .filter(Boolean)
    .map((c: any) => {
      const status = normalizeStatus(c);
      return {
        type: "cancel" as const,
        typeLabel: "주문 취소",
        claim_code: optionalString(c.claim_code ?? c.cancel_code ?? c.code),
        date: optionalString(c.cancel_date ?? c.claim_date ?? c.date),
        complete_date: optionalString(
          c.cancel_complete_date ?? c.complete_date
        ),
        reason: optionalString(c.reason ?? c.cancel_reason),
        reason_detail: optionalString(c.reason_detail ?? c.detail ?? c.memo),
        ...status,
        refund_amount: optionalNumber(
          c.refund_amount ?? c.actual_refund_amount
        ),
        refund_method: optionalString(c.refund_method ?? c.refund_method_name),
        items: mapClaimItems(c.items),
        isSynthesizedFromItems: false,
      };
    });

  // 2. Process Cafe24 Embed Return
  const returns: ClaimRecord[] = (
    Array.isArray(rawReturns) ? rawReturns : [rawReturns]
  )
    .filter(Boolean)
    .map((r: any) => {
      const status = normalizeStatus(r);
      return {
        type: "return" as const,
        typeLabel: "반품",
        claim_code: optionalString(r.claim_code ?? r.return_code ?? r.code),
        date: optionalString(r.return_date ?? r.claim_date ?? r.date),
        complete_date: optionalString(
          r.return_complete_date ?? r.complete_date
        ),
        reason: optionalString(r.reason ?? r.return_reason),
        reason_detail: optionalString(r.reason_detail ?? r.detail ?? r.memo),
        ...status,
        refund_amount: optionalNumber(
          r.refund_amount ?? r.actual_refund_amount
        ),
        refund_method: optionalString(r.refund_method ?? r.refund_method_name),
        items: mapClaimItems(r.items),
        isSynthesizedFromItems: false,
      };
    });

  // 3. Process Cafe24 Embed Exchange
  const exchanges: ClaimRecord[] = (
    Array.isArray(rawExchanges) ? rawExchanges : [rawExchanges]
  )
    .filter(Boolean)
    .map((e: any) => {
      const status = normalizeStatus(e);
      return {
        type: "exchange" as const,
        typeLabel: "교환",
        claim_code: optionalString(e.claim_code ?? e.exchange_code ?? e.code),
        date: optionalString(e.exchange_date ?? e.claim_date ?? e.date),
        complete_date: optionalString(
          e.exchange_complete_date ?? e.complete_date
        ),
        reason: optionalString(e.reason ?? e.exchange_reason),
        reason_detail: optionalString(e.reason_detail ?? e.detail ?? e.memo),
        ...status,
        refund_amount: optionalNumber(
          e.refund_amount ?? e.actual_refund_amount
        ),
        refund_method: optionalString(e.refund_method ?? e.refund_method_name),
        additional_payment_amount: optionalNumber(
          e.additional_payment_amount ?? e.additional_payment
        ),
        items: mapClaimItems(e.items),
        isSynthesizedFromItems: false,
      };
    });

  // 4. Item level inspection and automatic synthesis for unhandled claim items
  const itemLevelClaims: ClaimItemInfo[] = [];
  let cancelledItemCount = 0;
  let returnedItemCount = 0;
  let exchangeItemCount = 0;

  rawItems.forEach((it: any) => {
    const code = getOrderStatusCode(it);
    const isCancelled = code.startsWith("C") || it.canceled === "T";
    const isReturned = code.startsWith("R");
    const isExchange = code.startsWith("E") || code === "N01";

    if (isCancelled) cancelledItemCount += 1;
    if (isReturned) returnedItemCount += 1;
    if (isExchange) exchangeItemCount += 1;

    if (isCancelled || isReturned || isExchange) {
      const status = normalizeStatus(it);
      const claimItemInfo: ClaimItemInfo = {
        product_name: optionalString(it.product_name),
        item_no: it.item_no,
        product_no: it.product_no,
        order_item_code: optionalString(it.order_item_code),
        product_code: optionalString(it.product_code),
        quantity: optionalNumber(it.quantity),
        claim_quantity: optionalNumber(it.claim_quantity),
        product_price: optionalNumber(it.product_price),
        option_price: optionalNumber(it.option_price),
        option_value: optionalString(it.option_value),
        option_code: optionalString(it.option_code),
        claim_code: optionalString(it.claim_code),
        order_status: status.status,
        status_text: status.status_text,
        status_code: status.status,
      };
      itemLevelClaims.push(claimItemInfo);
    }
  });

  const totalItemCount = rawItems.length;
  const activeItemCount = Math.max(
    0,
    totalItemCount - (cancelledItemCount + returnedItemCount)
  );

  const hasCancellations = cancellations.length > 0;
  const hasReturns = returns.length > 0;
  const hasExchanges = exchanges.length > 0;
  const hasClaims = hasCancellations || hasReturns || hasExchanges;

  const isFullCancelled =
    totalItemCount > 0 && cancelledItemCount === totalItemCount;
  const isPartialCancelled = cancelledItemCount > 0 && !isFullCancelled;

  let overallStatusText = "정상 결제";
  let overallStatusBadgeClass = "badge-blue";

  if (isRefund) {
    overallStatusText = "환불 완료";
    overallStatusBadgeClass = "badge-red";
  } else if (isFullCancelled) {
    overallStatusText = "전체 취소";
    overallStatusBadgeClass = "badge-red";
  } else if (isPartialCancelled) {
    overallStatusText = "부분 취소";
    overallStatusBadgeClass = "badge-warning";
  } else if (hasReturns) {
    overallStatusText = "반품 발생";
    overallStatusBadgeClass = "badge-red";
  } else if (hasExchanges) {
    overallStatusText = "교환 진행";
    overallStatusBadgeClass = "badge-warning";
  }

  return {
    hasClaims,
    hasCancellations,
    hasReturns,
    hasExchanges,
    isFullCancelled,
    isPartialCancelled,
    overallStatusText,
    overallStatusBadgeClass,
    cancellations,
    returns,
    exchanges,
    cancelledItemCount,
    returnedItemCount,
    exchangeItemCount,
    totalItemCount,
    activeItemCount,
    reducedPaymentAmount,
    reducedOrderCompositionAmount,
    itemLevelClaims,
  };
}
